import { lazy, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Col, Container, Row } from 'react-bootstrap';
import { v5 as uuidv5 } from 'uuid';
import { ExpertTiersAd } from '@medifind/ads';
import { FindADoctorAd } from '@medifind/ads';
import { SignUpAd } from '@medifind/ads';
import { SymptomCheckerAd } from '@medifind/ads';
import { MediFindMoments } from '@medifind/ads';
import { WidgetContainer } from '@medifind/containers';
import { useLazyComponentPreload, useResultLimit, useResultRouteQueryResponse, useUrlPathLocation } from '@medifind/hooks';
import { useBookmarkLoader, usePermalinkLoader, useSearchPageBookmarkPermalink } from '@medifind/hooks';
import { doctorSearchResults } from '@medifind/interface';
import { usePageChanger, usePageHooks, useRouter, useSortChanger } from '@medifind/router';
import { DoctorCard, DoctorFiltersGroupV2, FilterSideContainer, Grid } from '@medifind/search-results';
import { BreadcrumbLinks, SEO } from '@medifind/seo';
import {
  InsuranceNotKnownDisclaimer,
  MFLazyLoad,
  SchedulesOnlineDisclaimer,
  getStaticFeatureFlag,
  useInsuranceNotKnownDisclaimerPosition,
  useSchedulesOnlineDisclaimerPosition,
} from '@medifind/shared-basic-components';
import { getValueMediaBreakpointDown } from '@medifind/utils';
import { fillTemplate, getNullList } from '@medifind/utils';
import { useMediaQueryUp } from '@medifind/utils';
import {
  clearDoctorFilters,
  doctorFiltersDependencies,
  useDoctorAvailabilityFilter,
  useDoctorGenderFilter,
  useDoctorInsuranceFilter,
  useDoctorLanguageFilter,
  useDoctorSearchRadiusFilter,
  useDoctorSecondarySpecialtyFilter,
  useDoctorYearsExpFilter,
  useFilterContext,
  useLocationStore,
  useRouteStore,
  withFilters,
} from '@medifind/zustand';
import { logEventGA4 } from '@medifind/zustand';
import { DoctorsGridDisclaimer } from '../../components/disclaimers';
import DisplayConditionPopoverHandler, { ConditionSearchPopoverButton } from './displayConditionPopoverHandler';
import styles from './find-specialist.module.scss';

const DoctorResultFilterList = lazy(() => import('@medifind/lazy-loadable/lib/search-results/LazyDoctorResultFilterList'));
const ByLocationWidget = lazy(() => import('@medifind/lazy-loadable/lib/shared-basic-components/LazyByLocationWidget'));
const ProgrammaticFAQ = lazy(() => import('./programatic-faqs'));

const sortItems = [
  { display: 'Relevance', value: 'relevance', isDefault: true },
  { display: 'Distance', value: 'distance' },
  { display: 'Expertise', value: 'rating' },
];

// Render ad's
const AdsContainer = () => {
  return (
    <div className={`${styles['ads__container']}`}>
      <SignUpAd tagLine="Save doctors for later" loginMessage="You can save doctors with a MediFind account." />
      <FindADoctorAd />
      <SymptomCheckerAd />
      <ExpertTiersAd />
    </div>
  );
};

/* __ROUTE_PATH__=/specialty/:specialtySlug/near-me */
/* __ROUTE_PATH__=/specialty/:specialtySlug/:countryCode?/:stateProvinceCode?/:city? */
const FindSpecialist = () => {
  usePageHooks();
  const filters = useFilterContext();
  const router = useRouter();

  const {
    query: { specialtySlug },
    pathParams: { countryCode: pathCountryCode, stateProvinceCode: pathStateProvinceCode, city: pathCity },
  } = router;
  const [size, setSize] = useState(25);
  useLazyComponentPreload([DoctorResultFilterList, ProgrammaticFAQ]);
  const {
    pageTitle,
    pageDescription,
    codingList,
    nameString: specialtyNameString,
    specialtySlug: newSpecialtySlug,
    specialty,
    role,
    notFound: specialtyNotFound,
  } = useRouteStore((state) => state?.params?.specialty || {});
  const conditionPopOverButtonRef = useRef(null);
  const isSmallDesktopAndAbove = useMediaQueryUp('lg');

  const location = useLocationStore();
  const { urlLocation, isLoading: isUrlLocationLoading, invalidLocation } = useUrlPathLocation();

  const { sort, handleSortChange, changeSortManualURLModification } = useSortChanger('sort', sortItems.find((x) => x.isDefault).value);
  const { page, handlePageChange } = usePageChanger('page', [...filters.valueDependencies, specialtySlug, location, sort]);
  usePermalinkLoader({
    onLoad: ({ data: { sort } }) => changeSortManualURLModification(sort),
    filterList: filters.filterList,
  });
  useBookmarkLoader({
    onLoad: ({ data: { sort } }) => changeSortManualURLModification(sort),
    filterList: filters.filterList,
  });

  const { resultLimitAchieved, resultLimitMessage } = useResultLimit({ page, size });

  const filterDepsChange =
    filters.checkFilterDependencies([specialtySlug, pathCountryCode, pathStateProvinceCode, pathCity]) && !resultLimitAchieved;
  const locationProvided = pathCountryCode || pathStateProvinceCode;
  const locationChange = (locationProvided && urlLocation && !isUrlLocationLoading) || !locationProvided;

  const {
    error,
    notFound: resultNotFound,
    unauthorized,
    results,
    featuredResults,
    totalResults,
    scoresCount,
  } = useResultRouteQueryResponse({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['specialtySearch', page, sort, specialtySlug, location, urlLocation, size, ...filters.dependencies].filter(Boolean),
    queryFn: ({ signal }) => {
      const query = {
        specialty: [...new Set([specialtySlug, doctorSecondarySpecialty].filter(Boolean))],
        radius: doctorSearchRadius || null,
        lat: location.lat,
        lon: location.lon,
        country: location.countryCode,
        state: location.stateProvinceCode,
        fidelity: location.fidelity,
        languages: doctorLanguage,
        size,
        page,
        sort: sort,
        gender: doctorGender || null,
        doctorYearsExperience: doctorYearsExperience,
        type: 'specialtySearch',
        showFeaturedCards: true,
        telemedicine: doctorAvailability.includes('telemedicine'),
        acceptsNewPatients: doctorAvailability.includes('acceptsNewPatients'),
        appointmentAssistOptIn: doctorAvailability.includes('appointmentAssistOptIn'),
        insurances: doctorInsurance,
      };
      return doctorSearchResults(query, signal);
    },
    enabled: !!(filterDepsChange && locationChange && specialtySlug),
    onError: (error) => {
      if (error !== null) {
        logEventGA4(
          'exception',
          { type: 'specialty-doctors', method: error.code, description: error.message },
          uuidv5(`specialty_doctors`, '00000000-0000-0000-0000-000000000000'),
        );
      }
    },
  });

  const schedulesOnlineDisclaimerPosition = useSchedulesOnlineDisclaimerPosition({ featuredResults, results });
  const insuranceNotKnownDisclaimerPosition = useInsuranceNotKnownDisclaimerPosition({ featuredResults, results });

  const {
    doctorAvailability,
    doctorYearsExperience,
    doctorGender,
    doctorLanguage,
    doctorSearchRadius,
    doctorSecondarySpecialty,
    doctorInsurance,
  } = filters.values;

  // This is for if a specialty from the API was renamed to something else.
  // ex: Heart Transplant -> Heart Replacement
  useEffect(() => {
    if (newSpecialtySlug && newSpecialtySlug !== specialtySlug) {
      router.history.replace(
        `/specialty/${[newSpecialtySlug, pathCountryCode, pathStateProvinceCode, pathCity].filter(Boolean).join('/')}`,
      );
    }
  }, [newSpecialtySlug]);

  useSearchPageBookmarkPermalink(
    null,
    'find-specialist',
    {
      specialtySlug,
      specialty,
      specialtyNameString,
      urlLocation: { countryCode: pathCountryCode, stateProvinceCode: pathStateProvinceCode, city: pathCity },
    },
    {},
    filters.filterList,
    [...filters.dependencies, specialtySlug, specialty, specialtyNameString, pathCountryCode, pathStateProvinceCode, pathCity],
  );

  // Filter sort options
  const validSortItems = useMemo(() => {
    let tempSortItems = sortItems;
    tempSortItems = location.fidelity > 3 ? tempSortItems.filter(({ value }) => value !== 'distance') : tempSortItems;
    return tempSortItems;
  }, [location.fidelity]);

  // If the sort is no longer valid, change it
  useEffect(() => {
    if (!validSortItems.find((s) => s.value === sort)) handleSortChange(validSortItems[0].value);
  }, [validSortItems]);

  useEffect(() => {
    filters.setFilterDependencies([specialtySlug, pathCountryCode, pathStateProvinceCode, pathCity]);
  }, [specialtySlug, pathCountryCode, pathStateProvinceCode, pathCity]);
  if (specialtyNotFound || resultNotFound || (locationProvided && invalidLocation)) {
    throw new Error(404);
  }
  const adTargets = {
    pageType: 'specialty-provider-search',
  };

  return (
    <>
      {pageTitle && <SEO title={`${pageTitle} | MediFind`} description={pageDescription} />}
      <Container className={styles['find-best-body']}>
        {/* grid / common conditions  */}
        <ConditionSearchPopoverButton
          screenReaderText="Click to show refine health conditions filter"
          label="Refine by health conditions"
          className={styles['popover-button']}
          ref={conditionPopOverButtonRef}
        />
        <Row className={styles['find-best-body__conditions-results-container']}>
          {/* common conditions */}
          <Col
            md={{ span: 12, order: 'first' }}
            lg={{ span: 4, order: 'first' }}
            xl={{ span: 3, order: 'first' }}
            className={styles['condition-grid']}
          >
            {/* ) : (*/}
            {/*  <Skeleton*/}
            {/*    height={600}*/}
            {/*    showSkeleton={getValueMediaBreakpointDown({ xs: false, sm: false, md: false, default: true })}*/}
            {/*    style={{ borderRadius: '20px' }}*/}
            {/*  />*/}
            {/* )}*/}

            <FilterSideContainer
              className="hide--md"
              title="Customize your search results with filters"
              lazyLoadOptions={{ height: 600, resize: true, checkOnLoad: true }}
              clearFilters={clearDoctorFilters}
            >
              <DoctorResultFilterList />
            </FilterSideContainer>
            {isSmallDesktopAndAbove && <MediFindMoments type="rectangle" targeting={{ ...adTargets, position: 'left1' }} />}

            {/* {contitionListCategoryId ? (*/}
            <DisplayConditionPopoverHandler
              specialty={specialty}
              role={role}
              codingList={codingList}
              title={role ? `Common Conditions Treated by ${role}s` : `Common Conditions`}
              popupButton={conditionPopOverButtonRef}
              popoverAt="md"
            />
            {isSmallDesktopAndAbove && <AdsContainer />}
          </Col>
          <Col sm={12} lg={8} xl={9} className={styles['find-best-body__body-container']}>
            <h2 className={'hide-h-tag'}>{role} Search Results</h2>
            {urlLocation?.locationString && (
              <h2 className={'hide-h-tag'}>
                {fillTemplate(
                  'MediFind found [totalResults:[totalResults]] specialists [city: near][mediumLocationString&!city: in][mediumLocationString: [mediumLocationString]]',
                  { totalResults, ...urlLocation },
                )}
              </h2>
            )}
            <WidgetContainer className={classNames(styles['find-best-body__widget-container'])}>
              {/* filter options (location/radius) */}
              <DoctorFiltersGroupV2
                sortItems={validSortItems}
                totalResults={totalResults}
                onSortByChange={({ value }) => handleSortChange(value)}
                sortValue={sort}
                classes={{
                  filters: styles['filter-header'],
                  filterHeader: styles['filter-header'],
                  locationContainer: styles['location-container'],
                }}
                smallAt="md"
                expertiseFilter={{ display: false }}
                adOptions={{ pageType: 'specialty-provider-search' }}
              />
              {/* doctor results*/}
              <Grid
                error={error || resultLimitAchieved}
                errorMessage={resultLimitMessage}
                lazyLoad={{
                  offset: 400,
                  height: getValueMediaBreakpointDown({ xs: 448, sm: 448, md: 244, default: 244 }),
                  resize: true,
                  noSuspense: true,
                }}
                insertResult={[
                  ...[2, 6, 10, 14, 18, 22].map((n, i) => ({
                    renderAfter: n,
                    render: (
                      <>
                        {n === 2 && !isSmallDesktopAndAbove ? (
                          <MediFindMoments type="rectangle" targeting={{ ...adTargets, position: `mid${i + 1}` }} />
                        ) : n > 2 ? (
                          <MediFindMoments
                            type="rectangle"
                            targeting={{ ...adTargets, position: `mid${!isSmallDesktopAndAbove ? i + 1 : i}` }}
                          />
                        ) : (
                          <></>
                        )}
                      </>
                    ),
                    dontShowAtEnd: i === 0 ? false : true,
                  })),
                  {
                    renderBefore: schedulesOnlineDisclaimerPosition + 1,
                    render: <SchedulesOnlineDisclaimer />,
                    dontShowAtEnd: true,
                  },
                  {
                    renderBefore: insuranceNotKnownDisclaimerPosition + 1,
                    render: <InsuranceNotKnownDisclaimer />,
                    dontShowAtEnd: true,
                  },
                ]}
                items={(featuredResults ?? [])
                  .map((doctor, i) => (
                    <DoctorCard
                      key={`doctor-feat-${i}`}
                      doctor={doctor}
                      showConditionCount={false}
                      priority={3}
                      smallAt={['xs', 'sm', 'lg']}
                    />
                  ))
                  .concat(
                    (results || getNullList(size)).map((doctor, i) => {
                      return (
                        <DoctorCard key={`doctor-${i}`} doctor={doctor} priority={3} smallAt={['xs', 'sm', 'lg']} showPhoneButton={true} />
                      );
                    }),
                  )}
                id={'find-best-doctors'}
                size={size}
                maxColumnCount={1}
                pagination={{
                  page,
                  totalResults: results && totalResults,
                  type: 'eventWithRouterShallowChange',
                  onPageChange: handlePageChange,
                  scrollOffset: 200,
                  forceScroll: true,
                  wideLayout: true,
                  requireLogin: {
                    errorMessage: `Sign in or create a free MediFind account to see more ${specialtyNameString}s. View, save, and contact these experts to find the care you deserve.`,
                    errorTitle: `More Expertise Awaits`,
                    afterPage: 1,
                  },
                }}
                noBorder
                addGutters
                bottomDisclaimer={results?.length ? <DoctorsGridDisclaimer /> : null}
              />
            </WidgetContainer>
          </Col>
        </Row>
      </Container>
      <MFLazyLoad height={6000} offset={400} checkOnChange={results && totalResults < 3}>
        <ProgrammaticFAQ {...{ slug: specialtySlug, /* conditionList, */ urlLocation, specialty }} />
      </MFLazyLoad>
      <MFLazyLoad height={600} offset={400}>
        <ByLocationWidget
          baseUrl={`/specialty/${specialtySlug}`}
          title={`${specialtyNameString}s by ${urlLocation?.stateProvinceCode ? 'city' : 'state'}`}
        />
      </MFLazyLoad>
      {!isSmallDesktopAndAbove && <AdsContainer />}
      <Container>
        <Row>
          <Col>
            <BreadcrumbLinks
              items={[
                { name: 'Homepage', link: '/' },
                ...[
                  specialtyNameString
                    ? {
                        name: `${specialtyNameString}s${!urlLocation?.locationString ? ' Near Me' : ''}`,
                        link: `/specialty/${newSpecialtySlug}`,
                      }
                    : null,
                ],
                // should always follow path params, not location filter
                ...[urlLocation?.locationString ? { name: urlLocation.locationString, link: router.wouterLocation } : null],
              ]}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default withFilters(
  [
    useDoctorGenderFilter,
    useDoctorYearsExpFilter,
    useDoctorSecondarySpecialtyFilter,
    useDoctorLanguageFilter,
    useDoctorSearchRadiusFilter,
    useDoctorAvailabilityFilter,
    ...(getStaticFeatureFlag('INSURANCE_FILTER') ? [useDoctorInsuranceFilter] : []),
  ],
  [doctorFiltersDependencies],
  [clearDoctorFilters],
)(FindSpecialist);
